import type { ProspectClosingChecklistDto, ProspectLeaseSigningStatus } from '../../services/tenantApi';
import { closingMembersFromChecklistRows } from './closing-roster';
import {
  applyLeaseSigningStatus,
  emptyClosingReviewState,
  emptyGroupState,
  emptyUtilitiesKingstonSetupContext,
  type ClosingChecklist,
  type ClosingGroupState,
  type ClosingPersonalState,
} from './closing-model';

export function groupStateFromClosingChecklist(
  dto: ProspectClosingChecklistDto,
  current: ClosingGroupState,
): ClosingGroupState {
  return {
    ...current,
    leaseSent: dto.group.leaseSent,
    signedByPersonId: dto.group.signedByPersonId,
    leaseFileName: dto.group.leaseFileName,
    leaseDocumentId: dto.group.leaseDocumentId,
    utilityFileName: dto.group.utilityFileName,
    utilityDocumentId: dto.group.utilityDocumentId,
    utilitySetupReview: dto.group.reviews.utilitySetup ?? emptyClosingReviewState(),
    utilitiesKingston: dto.group.utilitiesKingston ?? emptyUtilitiesKingstonSetupContext(),
    housemateAgreementReview: dto.group.reviews.housemateAgreement ?? emptyClosingReviewState(),
    housemateSignedFileName: dto.group.housemateSignedFileName,
    housemateDocumentId: dto.group.housemateDocumentId,
    housemateTemplateReady: dto.group.housemateTemplateReady,
  };
}

/** Same checklist the closing page uses, so the sidebar ring can load before that page opens. */
export function closingChecklistForSidebar(
  dto: ProspectClosingChecklistDto,
  lease: ProspectLeaseSigningStatus | null,
  viewerPersonId: string | null,
): ClosingChecklist {
  const personal = personalFromClosingChecklist(dto);
  return {
    selected: true,
    members: closingMembersFromChecklistRows(dto.members ?? [], personal, viewerPersonId),
    personal,
    group: applyLeaseSigningStatus(groupStateFromClosingChecklist(dto, emptyGroupState()), lease),
  };
}

export function personalFromClosingChecklist(
  dto: ProspectClosingChecklistDto,
): Record<string, ClosingPersonalState> {
  const personal: Record<string, ClosingPersonalState> = {};
  for (const [personId, row] of Object.entries(dto.personal)) {
    personal[personId] = {
      applicantId: row.applicantId,
      headshotDocumentId: row.headshotDocumentId,
      padFileName: row.padFileName,
      padDocumentId: row.padDocumentId,
      padSigned: row.padSigned,
      voidChequeFileName: row.voidChequeFileName,
      voidChequeDocumentId: row.voidChequeDocumentId,
      lastMonthRentETransferFileName: row.lastMonthRentETransferFileName ?? null,
      lastMonthRentETransferDocumentId: row.lastMonthRentETransferDocumentId ?? null,
      lastMonthRentETransferSent: row.lastMonthRentETransferSent,
      lastMonthRentETransferReceived: row.lastMonthRentETransferReceived,
      eTransferAmountLabel: row.eTransferAmountLabel,
      padReview: row.reviews.pad ?? emptyClosingReviewState(),
      voidChequeReview: row.reviews.voidCheque ?? emptyClosingReviewState(),
      eTransferSentReview: row.reviews.lastMonthRentETransferSent ?? emptyClosingReviewState(),
      eTransferReceivedReview: row.reviews.lastMonthRentETransferReceived ?? emptyClosingReviewState(),
    };
  }
  return personal;
}
